import React, { useEffect, useState } from 'react';
import './Details.css';
import { Link } from 'react-router-dom';
import { api } from '../api';

// Source symbol (?s=) comes from the application record (api/applications.php)
function getSource(app) {
  return app?.source ?? 's';
}

function imageUrl(element) {
  if (element.imageSrc && element.imageSrc.startsWith('http')) return element.imageSrc;
  return 'https://admin.tablemate.net/sites/default/files/' + (element.imageSrc || '').replace('public://', '');
}

// Parse category's application field as comma-separated list (supports multiple applications per category)
function categoryApplicationList(cat) {
  if (!cat.application) return [];
  return String(cat.application)
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

function isPublished(article) {
  return article && (article.status === 1 || article.status === '1');
}

function countKey(applicationName, categoryUri) {
  return `${applicationName}\u0000${categoryUri}`;
}

function Home() {
  const [applications, setApplications] = useState([]);
  const [categories, setCategories] = useState([]);
  const [articleCounts, setArticleCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    Promise.all([
      api.get('/api/applications.php'),
      api.get('/api/categories.php'),
    ])
      .then(async ([apps, cats]) => {
        const applicationList = Array.isArray(apps) ? apps : [];
        const categoryList = Array.isArray(cats) ? cats : [];
        const sources = [...new Set(applicationList.map((app) => getSource(app)))];
        const lists = await Promise.all(
          sources.map((source) =>
            api.get(`/api/articles.php?category=__all__&s=${encodeURIComponent(source)}`).catch(() => [])
          )
        );
        const counts = {};
        lists.forEach((list) => {
          (Array.isArray(list) ? list : []).forEach((article) => {
            if (!isPublished(article) || !article.category_code) return;
            const key = countKey(article.application, article.category_code);
            counts[key] = (counts[key] || 0) + 1;
          });
        });
        if (cancelled) return;
        setApplications(applicationList);
        setCategories(categoryList);
        setArticleCounts(counts);
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.error || 'Failed to load');
        setApplications([]);
        setCategories([]);
        setArticleCounts({});
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // For each application, get categories that list this application (category application is comma-separated list)
  // Only include applications that have at least one category
  const categoriesByApplication = applications
    .map((app) => ({
      application: app,
      categories: categories.filter((cat) =>
        categoryApplicationList(cat).includes(app.name)
      ),
    }))
    .filter(({ categories: cats }) => cats.length > 0);

  if (loading) {
    return (
      <div className="details-container-page">
        <div>
          <h2 className="detailTitle">App Sections</h2>
        </div>
        <p>Loading…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="details-container-page">
        <div>
          <h2 className="detailTitle">App Sections</h2>
        </div>
        <p style={{ color: '#c00' }}>{error}</p>
      </div>
    );
  }

  return (
    <div className="details-container-page">
      <div>
        <h2 className="detailTitle">App Sections</h2>
      </div>
      {categoriesByApplication.map(({ application: app, categories: cats }, index) => (
        <React.Fragment key={app.id}>
          {index > 0 && <hr />}
          <h3>{app.name}</h3>
          <div className="button-grid row">
            {cats.map((cat) => {
              const count = articleCounts[countKey(app.name, cat.uri)] || 0;
              return (
                <div className="details-button col-4" key={cat.uri}>
                  <div className="details-button-icon">
                    <Link to={`${cat.uri}?s=${getSource(app)}`}>
                      <img src={imageUrl(cat)} alt={cat.labelText || cat.uri} className="button-icon" />
                    </Link>
                    <span
                      className="category-count-badge"
                      title={`${count} ${count === 1 ? 'article' : 'articles'}`}
                    >
                      {count}
                    </span>
                  </div>
                  <div>{cat.labelText || cat.uri}</div>
                </div>
              );
            })}
          </div>
        </React.Fragment>
      ))}
      {applications.length === 0 && (
        <p style={{ color: '#888' }}>No applications yet. Add them in Admin → Applications.</p>
      )}
      {applications.length > 0 && categoriesByApplication.length === 0 && (
        <p style={{ color: '#888' }}>No articles for any application yet.</p>
      )}
    </div>
  );
}

export default Home;
